import React, { useState, useEffect, useCallback } from 'react';
import { getMyTasks, updateParticipatingTask } from '../services/api';
import { useToast } from '../context/ToastContext';
import './MyAssignedTasks.css';

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function MyAssignedTasks({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, done, all
  const { toast } = useToast();

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMyTasks();
      if (res.success) setTasks(res.tasks || []);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const toggleDone = async (task) => {
    const nextStatus = task.status === 'done' ? 'pending' : 'done';
    try {
      await updateParticipatingTask(task.tender_id, task.task_id, { status: nextStatus });
      fetchTasks();
      toast.success(nextStatus === 'done' ? 'Task marked complete' : 'Task reopened');
    } catch (err) {
      toast.error(err?.message || 'Failed to update task');
    }
  };

  const filtered = tasks.filter(t => {
    if (filter === 'active') return t.status !== 'done';
    if (filter === 'done') return t.status === 'done';
    return true;
  });

  const activeTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="my-tasks-section">
      <div className="my-tasks-header">
        <h2>My Assigned Tasks</h2>
        <div className="my-tasks-summary">
          <span className="my-tasks-count active">{activeTasks.length} active</span>
          <span className="my-tasks-count done">{doneTasks.length} done</span>
        </div>
      </div>

      <div className="my-tasks-filters">
        <button
          className={`my-tasks-filter-btn ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          Active ({activeTasks.length})
        </button>
        <button
          className={`my-tasks-filter-btn ${filter === 'done' ? 'active' : ''}`}
          onClick={() => setFilter('done')}
        >
          Completed ({doneTasks.length})
        </button>
        <button
          className={`my-tasks-filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({tasks.length})
        </button>
      </div>

      {loading ? (
        <div className="my-tasks-loading">Loading your tasks...</div>
      ) : filtered.length === 0 ? (
        <div className="my-tasks-empty">
          {filter === 'active'
            ? 'No active tasks assigned to you.'
            : filter === 'done'
            ? 'No completed tasks yet.'
            : 'No tasks assigned to you.'}
        </div>
      ) : (
        <div className="my-tasks-list">
          {filtered.map((task) => (
            <div
              key={`${task.tender_id}-${task.task_id}`}
              className={`my-task-card ${task.status === 'done' ? 'done' : ''} ${
                task.status !== 'done' && isOverdue(task.due_date) ? 'overdue' : ''
              }`}
            >
              <button
                className="my-task-checkbox"
                onClick={() => toggleDone(task)}
                aria-label={task.status === 'done' ? 'Mark incomplete' : 'Mark complete'}
              >
                {task.status === 'done' ? (
                  <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
                    <rect x="3" y="3" width="14" height="14" rx="3"/>
                  </svg>
                )}
              </button>
              <div className="my-task-body">
                <div className="my-task-title">{task.title}</div>
                <div className="my-task-meta">
                  <span className="my-task-tender" title={task.tender_title || ''}>
                    {task.tender_ref}
                  </span>
                  {task.buyer_name && (
                    <span className="my-task-buyer">{task.buyer_name}</span>
                  )}
                  {task.due_date && (
                    <span className={`my-task-due ${isOverdue(task.due_date) && task.status !== 'done' ? 'overdue' : ''}`}>
                      {formatDate(task.due_date)}
                    </span>
                  )}
                  <span className={`my-task-tender-status status-${task.tender_status}`}>
                    {task.tender_status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyAssignedTasks;
